import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useParams } from "react-router-dom";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import Select from "react-select";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  mapFilterDefinitionsToModalData,
  mergeModalSubmitIntoListFilter,
} from "helpers/metadataFilterHelpers";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import Spinners from "components/Common/Spinner";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import TableContainer from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import PreQuestioningAddEditModalData from "pages/AllCommonModal/QuestionTypeAddEditModal/pre-questioning-add-edit-modal";
import { useClientPreQuestionContext } from "libs/context/ClientPreQuestionContext";
import {
  submitClientPreQuestionFilterData,
  deleteClientPreQuestion,
} from "model/ClientPreQuestionModel";
import { fetchCommonJobOptions } from "model/JobModel";
import Loaders from "components/Common/Loaders";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const normalizePreQuestionFilterPayload = (raw = {}) => {
  const out = { ...raw };
  ["nameContains", "clientId", "jobId"].forEach((key) => {
    if (key in out) out[key] = strOrNull(out[key]);
  });
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) delete out[key];
  });
  return out;
};

const getSelectedOption = (options, value) =>
  (Array.isArray(options) ? options : []).find(
    (opt) => String(opt?.value ?? "") === String(value ?? "")
  ) || null;

const PreQuestioning = () => {
  const { clientId } = useParams();
  const { permissionData } = usePermissionContext() || {};
  const { fetchClientPreQuestionContextData, clientPreQuestionContextData } = useClientPreQuestionContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("pre-questioning");
  const initialFilterFetchedRef = useRef(false);
  const [modal1, setModal1] = useState(false);
  const toggleViewModal = () => setModal1(!modal1);
  const [filterForm, setFilterForm] = useState({
    search: "",
    clientId: clientId || "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    jobId: "",
  });
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [preQuestionData, setPreQuestionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
    clientId: null,
    jobId: null,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [preQuestioningAddEditModalOpen, setPreQuestioningAddEditModalOpen] = useState(false);
  const [itemId, setItemId] = useState(null);
  const initialFormData = { id: "", clientId: "", jobId: "", questions: [] };
  const [formData, setFormData] = useState(initialFormData);
  const [questionDetails, setQuestionDetails] = useState({});

  const filteredData = useMemo(() => preQuestionData, [preQuestionData]);

  const preQuestioningActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(
        permissionData?.RegisterPreQuestioningAccess ||
          permissionData?.ClientPreQuestioningAccess
      ),
      canEdit: Boolean(
        permissionData?.RegisterPreQuestioningAccess ||
          permissionData?.ClientPreQuestioningAccess
      ),
      canDelete: Boolean(
        permissionData?.RegisterPreQuestioningAccess ||
          permissionData?.ClientPreQuestioningAccess
      ),
    }),
    [permissionData]
  );

  const hasAnyPreQuestioningRowAction = useMemo(
    () => Object.values(preQuestioningActionPermissions).some(Boolean),
    [preQuestioningActionPermissions]
  );

  const preQuestioningFilterModalData = useMemo(
    () =>
      mapFilterDefinitionsToModalData(
        clientPreQuestionContextData?.filters || [],
        filterForm
      ),
    [clientPreQuestionContextData?.filters, filterForm]
  );
  const fetchFilterOptions = async () => {
    const jobRes = await fetchCommonJobOptions({});
    const jobs =
      jobRes?.data?.records ??
      jobRes?.data?.data ??
      jobRes?.data ??
      jobRes?.records ??
      [];
    const rows = Array.isArray(jobs) ? jobs : [];
    setJobOptions(
      rows
        .map((item) => {
          const value = String(
            item?.id ?? item?._id ?? item?.value ?? item?.jobId ?? ""
          ).trim();
          const label = String(
            item?.display ??
              item?.name ??
              item?.title ??
              item?.jobName ??
              item?.label ??
              ""
          ).trim();
          if (!value) return null;
          return { value, label: label || value };
        })
        .filter(Boolean)
    );
  };

  useEffect(() => {
    fetchClientPreQuestionContextData?.();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getPreQuestioningList({
      pageNumber: 1,
      pageSize,
      ...(clientId ? { clientId: strOrNull(clientId) } : {}),
    });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = clientPreQuestionContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: pascalToCamel(col?.field),
      type: col?.type,
    }));
    setTableMetaData(valueReModified || []);
  }, [clientPreQuestionContextData]);

  const getPreQuestioningList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = normalizePreQuestionFilterPayload({
      pageNumber: requestPayload.pageNumber,
      pageSize: requestPayload.pageSize,
      nameContains: requestPayload.nameContains,
      clientId: requestPayload.clientId,
      jobId: requestPayload.jobId,
      ...payloadOverrides,
    });

    delete payload.client;
    delete payload.clientName;
    delete payload.clientCompanyId;
    delete payload.companyId;
    delete payload.clientID;

    const response = await submitClientPreQuestionFilterData(payload);
    const responseData = response?.data || {};
    const listData = responseData?.records || responseData || [];
    const valueReModified = listData?.map((item = {}) => ({
      ...item,
      id: item?.id || item?._id || "",
      client: item?.client || item?.clientName || "",
      jobName: item?.jobName || item?.job_name || item?.job || "",
    }));

    if (response) {
      setPreQuestionData(valueReModified || []);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || 0,
      });
      setRequestPayload(normalizePreQuestionFilterPayload(payload));
    } else {
      setPreQuestionData([]);
    }
    setLoading(false);
  };


  const handleEdit = (row) => {
    setItemId(row?.id);
    setQuestionDetails(row);
    setPreQuestioningAddEditModalOpen(true)
  };

  const handleDelete = async (row) => {
    const id = String(row?.id || row?._id || "").trim();
    if (!id) {
      toast.error("Record id is missing");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Delete pre question?",
      text: "Are you sure you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;

    setLoading(true);
    try {
      const res = await deleteClientPreQuestion(id);
      if (res?.success !== false && (res?.success || res?.isSuccess || res?.data != null)) {
        toast.success(res?.message || "Deleted successfully");
        await getPreQuestioningList({ pageNumber: paginationMeta?.pageNumber || 1 });
        return;
      }
      toast.error(res?.message || "Unable to delete");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = async (modalFormPayload = {}) => {
    const mergedFilterForm = mergeModalSubmitIntoListFilter(
      filterForm,
      preQuestioningFilterModalData,
      modalFormPayload
    );
    setFilterForm(mergedFilterForm);
    await getPreQuestioningList({
      ...buildPayloadFromUpperForm(mergedFilterForm),
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const buildPayloadFromUpperForm = (form = {}) =>
    normalizePreQuestionFilterPayload({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: strOrNull(form?.search),
      clientId: strOrNull(form?.clientId),
      jobId: strOrNull(form?.jobId),
    });

  const handleUpperSearch = () => {
    getPreQuestioningList(buildPayloadFromUpperForm(filterForm));
  };

  const handleFilterFieldChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleJobSelectChange = (selectedOption) => {
    setFilterForm((prev) => ({
      ...prev,
      jobId: selectedOption?.value ?? "",
    }));
  };

  const handlePageChange = async (page) => {
    await getPreQuestioningList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getPreQuestioningList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* View Modal */}
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

          {/* 🔎 Search + Create Section */}
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Pre Questioning</h5>
                {clientId && preQuestioningActionPermissions.canCreate ? (
                  <div className="d-flex justify-content-end mb-3">
                    <Button
                      color="primary"
                      onClick={() => {
                        setItemId(null);
                        setQuestionDetails({});
                        setFormData({
                          ...initialFormData,
                          clientId: clientId || "",
                        });
                        setPreQuestioningAddEditModalOpen(true);
                      }}
                    >
                      + Create
                    </Button>
                  </div>
                ) : null}
              </div>
              <hr />

                            <LegacyFilterToolbar>
              <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--text">
                  <span className="manage-user-filter-label">Search</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="text"
                    name="search"
                    placeholder="Search by name"
                    className={LEGACY_FILTER_INPUT_CLASS}
                    value={filterForm.search}
                    onChange={handleFilterFieldChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleUpperSearch();
                      }
                    }}
                  />
                  </div>
                </Col>
                <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--autocomplete">
                  <span className="manage-user-filter-label">Client</span>
                  <div className="metadata-filter-field__control">
                  <AsyncClientSelect
                    key={`pre-questioning-client-${filterForm.clientId}`}
                    pageName="ClientMenu"
                    value={filterForm.clientId}
                    initialOption={buildClientAsyncFilterInitialOption(
                      filterForm.clientId,
                      filterForm[getClientAsyncFilterLabelFieldKey()]
                    )}
                    onChange={(option) =>
                      setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, option))
                    }
                    placeholder="Select Client"
                  />
                  </div>
                </Col>
                <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--dropdown">
                  <span className="manage-user-filter-label">Job</span>
                  <div className="metadata-filter-field__control">
                  <Select
                    options={jobOptions}
                    className="select2-selection"
                    placeholder="Select Job"
                    value={getSelectedOption(jobOptions, filterForm.jobId)}
                    onChange={handleJobSelectChange}
                    isClearable
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                  <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                  <div className="metadata-filter-toolbar__actions">
                  <Button
                    color="primary"
                    className="w-100 manage-user-search-btn"
                    onClick={handleUpperSearch}
                  >
                    <i className="fa fa-search me-1"></i>
                    Search
                  </Button>
                  </div>
                </Col>
              </Row>
              </LegacyFilterToolbar>


              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyPreQuestioningRowAction}
                  onEdit={
                    preQuestioningActionPermissions.canEdit ? handleEdit : undefined
                  }
                  onDelete={
                    preQuestioningActionPermissions.canDelete ? handleDelete : undefined
                  }
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"pre-questioning"}
                />
              ) : (
                // <Card className="border mb-3">
                //   <CardBody>
                //     <Spinners setLoading={setLoading} />
                //   </CardBody>
                // </Card>
                <Loaders />
              )}
            </CardBody>
          </Card>
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Pre Questioning"
            handleSubmit={handleFilterSubmit}
            filterData={preQuestioningFilterModalData}
          />
          <PreQuestioningAddEditModalData
            setPreQuestioningAddEditModalOpen={setPreQuestioningAddEditModalOpen}
            preQuestioningAddEditModalOpen={preQuestioningAddEditModalOpen}
            setFormData={setFormData}
            formData={formData}
            itemId={itemId}
            getQuestionTypeList={getPreQuestioningList}
            questionTypeDetails={questionDetails}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

PreQuestioning.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(PreQuestioning);
